

























































































































































import AlgoliaDebouncedSearchBox from '@algolia-search/components/atoms/DebouncedSearchBox/AlgoliaDebouncedSearchBox.vue';
import AlgoliaProductSuggestions from '@algolia-search/components/molecules/AlgoliProductSuggestions/AlgoliaProductSuggestions.vue';
import AutocompleteEmptyStateMain from '@algolia-search/components/organisms/EmptyStateMain/AutocompleteEmptyStateMain.vue';
import AutocompleteContentBlock from '@algolia-search/components/templates/ContentBlock/AutocompleteContentBlock.vue';
import { useAlgoliaInsights } from '@algolia-search/composables/useAlgoliaInsights';
import { useAlgoliaSearch } from '@algolia-search/composables/useAlgoliaSearch';
import { useAlgoliaSearchClient } from '@algolia-search/composables/useAlgoliaSearchClient';
import useAlgoliaSuggestions from '@algolia-search/composables/useAlgoliaSuggestions';
import {
  INDEX_CATEGORIES,
  INDEX_CONTENTFUL_PAGES,
  INDEX_PRODUCTS,
  INDEX_SUGGESTIONS
} from '@algolia-search/consts';
import { getResultsFromIndex } from '@algolia-search/helpers/indices';
import type { PushBlock } from '@algolia-search/models';
import useAlgoliaSearchStore from '@algolia-search/stores/algoliaSearch';
import { useAlgoliaConfigStore } from '@algolia-search/stores/config';
import { useAlgoliaPurchaseTracking } from '@algolia-search/stores/purchaseTracking';
import type {
  AlgoliaContentfulPage,
  AlgoliaHit,
  AlgoliaProduct,
  AlgoliaSection,
  AutocompleteCategoryIndex,
  AutocompleteContentfulPagesIndex,
  AutocompleteProductIndex
} from '@algolia-search/types';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  useContext,
  useFetch,
  useRoute,
  useRouter,
  watch
} from '@nuxtjs/composition-api';
import VaimoButton from 'atoms/VaimoButton.vue';
import VaimoLogo from 'atoms/VaimoLogo.vue';
import debounce from 'lodash.debounce';
import { storeToRefs } from 'pinia';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  AisAutocomplete,
  AisConfigure,
  AisHits,
  AisIndex,
  AisInstantSearch,
  AisStateResults
} from 'vue-instantsearch';

import { useMegaMenuMobileStore } from '~/diptyqueTheme/stores/megaMenuMobile';
import type { SearchModulesModel } from '~/diptyqueTheme/types/contentful';
import { useContentfulGQL } from '~/integrations/contentful/src/composables/useContentfulGQL';

export default defineComponent({
  name: 'AlgoliaSearchAutocomplete',
  components: {
    VaimoButton,
    AisInstantSearch,
    AisHits,
    AisConfigure,
    AisStateResults,
    AisIndex,
    AisAutocomplete,
    AlgoliaProductSuggestions,
    AlgoliaDebouncedSearchBox,
    VaimoLogo,
    AutocompleteContentBlock,
    AutocompleteEmptyStateMain,
    AlgoliaQuerySuggestions: () =>
      import(
        '@algolia-search/components/molecules/AlgoliaQuerySuggestions/AlgoliaQuerySuggestions.vue'
      ),
    AutocompleteEmptyStateSidebar: () =>
      import(
        '@algolia-search/components/organisms/EmptyStateSidebar/AutocompleteEmptyStateSidebar.vue'
      ),
    AutocompleteSidebar: () =>
      import(
        '@algolia-search/components/organisms/Sidebar/AutocompleteSidebar.vue'
      )
  },
  setup() {
    const {
      app: { $gtm, i18n, localeRoute }
    } = useContext();
    const router = useRouter();
    const { searchClient } = useAlgoliaSearchClient(true);
    const searchStore = useAlgoliaSearchStore();
    const {
      autocompleteDebounce,
      autocompleteMinimumChars,
      categoriesSuggestionsCount,
      productsSuggestionsCount,
      queriesSuggestionsCount
    } = useAlgoliaConfigStore();
    const { isSearchModalOpen } = storeToRefs(searchStore);
    const { query, queryResponse } = useContentfulGQL('searchModulesByKey');
    const { getIndex } = useAlgoliaSearch();
    const { addToRecentSearches } = useAlgoliaSuggestions();
    const megaMenuMobileStore = useMegaMenuMobileStore();
    const route = useRoute();
    const isSearchPage = computed(() => route.value.path.includes('search'));
    const purchaseTracking = useAlgoliaPurchaseTracking();
    const { sendAddToCartEvent, sendClickEvent } = useAlgoliaInsights();

    const searchQuery = ref<string>('');
    const autocompleteRef = ref(null);
    const emptySearchEntries = ref<SearchModulesModel | null>(null);
    const isPushItemAvailable = ref<boolean>(false);
    const isSectionsAvailable = ref<boolean>(false);
    const pushItemData = ref<PushBlock | undefined>(undefined);
    const sectionsData = ref<AlgoliaSection[]>([]);
    const hasReachedMinQueryLength = computed(
      (): boolean => searchQuery.value.length >= autocompleteMinimumChars
    );
    const hasSearchResults = computed(
      (): boolean =>
        hasReachedMinQueryLength.value &&
        (isPushItemAvailable.value || isSectionsAvailable.value)
    );

    const productIndexName = getIndex(INDEX_PRODUCTS);
    const categoriesIndexName = getIndex(INDEX_CATEGORIES);
    const contentfulPagesIndexName = getIndex(INDEX_CONTENTFUL_PAGES, true);
    const querySuggestionsIndexName = getIndex(INDEX_SUGGESTIONS);

    const sendAlgoliaClickEvent = (hit: AlgoliaHit, index: string) => {
      sendClickEvent(index, [hit.objectID], [hit.__position], hit.__queryID);
    };

    const onSelectSuggestion = (selectedItem: AlgoliaHit) => {
      if (selectedItem) {
        searchQuery.value = selectedItem.query;

        sendAlgoliaClickEvent(selectedItem, querySuggestionsIndexName);
        addToRecentSearches(searchQuery.value);
        handleSubmit();
      }
    };

    const handleKeydown = (event: KeyboardEvent, query?: string) => {
      if (query) {
        searchQuery.value = query;
      }

      if (event.key === 'Enter') {
        handleSubmit(true);
      }
    };

    const toggleSearchModal = (value: boolean): void => {
      if (searchStore.getSearchModalOpen === value) {
        return;
      }

      searchStore.setSearchModalOpen(value);

      if (!value) {
        (document.activeElement as HTMLElement).blur();
      }
    };

    const handleFocus = () => {
      $gtm.push({
        event: 'clickSearch',
        searchType: 'Products'
      });
      toggleSearchModal(true);
    };

    const handleInput = (value: string) => {
      // AisSearchBox doesn't support v-model correctly,
      // so we need to update the value manually
      if (value) {
        if (value.length < autocompleteMinimumChars) {
          return;
        }

        searchQuery.value = value;
      } else {
        searchQuery.value = ''; // prevents undefined value
      }

      if (isSearchPage.value && hasReachedMinQueryLength.value) {
        handleSearchPageInput(value);
      }

      searchStore.setSearchTerm(searchQuery.value);
    };

    const handleSearchPageInput = debounce((value: string) => {
      router.replace({
        path: route.value.path,
        query: {
          q: value
        }
      });
      searchStore.search(value);
    }, autocompleteDebounce);

    const handleSubmit = async (shouldAddToRecentSearch = false) => {
      if (!searchQuery.value || !searchStore.getSearchModalOpen) {
        handleFocus();
        return;
      }

      // Add the search to recent searches
      if (shouldAddToRecentSearch && searchQuery.value.trim()) {
        addToRecentSearches(searchQuery.value);
      }

      await router.push(
        localeRoute({ name: 'algolia-search', query: { q: searchQuery.value } })
      );

      searchStore.setSearchModalOpen(false);
      megaMenuMobileStore.hide();

      setTimeout(() => {
        searchQuery.value = '';
      }, 100);
    };

    const closeButtonHandler = () => {
      toggleSearchModal(false);

      $gtm.push({
        event: 'click_close_popin'
      });
    };

    useFetch(async () => {
      await query('getSearchModulesByKey', {
        locale: i18n.localeProperties.contentfulLocale,
        storeview: i18n.localeProperties.storeview
      });
      emptySearchEntries.value =
        queryResponse.value?.searchModulesCollection?.items?.[0] ?? null;
      searchStore.setTrendingNowBlockOnSearchPageEnabled(
        emptySearchEntries.value?.enableTrendingNowBlockOnSearchPage
      );
    });

    onMounted(() => {
      watch(isSearchModalOpen, (value: boolean) => {
        if (!isSearchPage.value) {
          document.documentElement.classList.toggle('no-scroll', value);
        }
      });
    });

    onBeforeUnmount(() => {
      document.documentElement.classList.remove('no-scroll');
    });

    const getPushItem = (indices: AutocompleteProductIndex[]): void => {
      const results = getResultsFromIndex(indices, productIndexName);

      pushItemData.value = results?.userData?.[0];
      isPushItemAvailable.value = !!pushItemData.value;
    };

    const getSections = (
      indices: AutocompleteCategoryIndex[] | AutocompleteContentfulPagesIndex[]
    ): void => {
      const categoriesResults = getResultsFromIndex(
        indices as AutocompleteCategoryIndex[],
        categoriesIndexName
      );
      const contentfulResults = getResultsFromIndex(
        indices as AutocompleteContentfulPagesIndex[],
        contentfulPagesIndexName
      );

      const categoryHits = (categoriesResults?.hits || []).map(
        (hit, index) => ({
          ...hit,
          __position: index + 1,
          __queryID: categoriesResults?.queryID || ''
        })
      );

      const formattedContentfulPages = (contentfulResults?.hits || []).map(
        (hit: AlgoliaContentfulPage, index) => ({
          ...hit,
          name: hit.title,
          __position: index + 1,
          __queryID: contentfulResults?.queryID || ''
        })
      );

      sectionsData.value = [...categoryHits, ...formattedContentfulPages];
      isSectionsAvailable.value = sectionsData.value.length > 0;
    };

    watch(
      () => autocompleteRef.value?.state?.indices,
      (newIndices) => {
        if (newIndices && hasReachedMinQueryLength.value) {
          getPushItem(newIndices);
          getSections(newIndices);
        }
      },
      { deep: true }
    );

    const handleHitClick = (hit: AlgoliaHit, index: string) => {
      sendAlgoliaClickEvent(hit, index);
      searchStore.setSearchModalOpen(false);
      megaMenuMobileStore.hide();
    };

    const handleAddHitToCart = (product: AlgoliaProduct) => {
      sendAddToCartEvent(
        'Added to cart',
        productIndexName,
        [product.objectID],
        product.__queryID
      );
      purchaseTracking.addPurchasedHit(product);
    };

    return {
      searchClient,
      productIndexName,
      searchQuery,
      hasReachedMinQueryLength,
      handleInput,
      handleSubmit,
      handleFocus,
      toggleSearchModal,
      closeButtonHandler,
      isSearchModalOpen,
      autocompleteDebounce,
      emptySearchEntries,
      getPushItem,
      getSections,
      onSelectSuggestion,
      handleKeydown,
      categoriesIndexName,
      querySuggestionsIndexName,
      productsSuggestionsCount,
      categoriesSuggestionsCount,
      queriesSuggestionsCount,
      hasSearchResults,
      contentfulPagesIndexName,
      pushItemData,
      sectionsData,
      isPushItemAvailable,
      isSectionsAvailable,
      autocompleteRef,
      isSearchPage,
      handleAddHitToCart,
      handleHitClick
    };
  },
  watch: {
    $route() {
      if (this.isSearchPage) {
        document.documentElement.classList.remove('no-scroll');
        return;
      }

      this.toggleSearchModal(false);
    }
  }
});
